import { FiCheck, FiCheckSquare } from "react-icons/fi";
import { PuffLoader } from "react-spinners";
import type { MealMonitoringDetailItem } from "../../../../../api/meal/types/mealMonitoringDetailType";

interface MealDetailItemProps {
  item: MealMonitoringDetailItem;
  activeTab: string;
  isCleaningService: boolean;
  canComplete: boolean;
  isCompleting: boolean;
  onComplete: (item: MealMonitoringDetailItem) => void;
}

const NOT_TAKING_MEAL_TAB_TITLE = "Tidak Ambil Makan";
const UNALLOCATED_TAB_TITLE = "Belum Dialokasikan";

const getTypeBadgeClassName = (type: string): string => {
  switch (type.trim().toLowerCase()) {
    case "out of stock":
      return "border-[#FF5C5C] text-[#FF5C5C]";
    case "absent":
      return "border-[#FFA26B] text-[#FF9F43]";
    case "late":
      return "border-[#FFC107] text-[#C99400]";
    case "not allocated":
      return "border-[#B5B5B5] text-[#888888]";
    default:
      return "border-[#48d9a9] text-[#32c896]";
  }
};

const getReasonBadgeClassName = (reason: string): string => {
  const normalized = reason.trim().toLowerCase();

  if (normalized === "not taking meal") {
    return "border-[#FF5C5C] text-[#FF5C5C]";
  }

  if (normalized === "out of office") {
    return "border-[#6C8CFF] text-[#5470E0]";
  }

  if (normalized === "training") {
    return "border-[#FFC107] text-[#C99400]";
  }

  if (normalized.startsWith("makan diluar")) {
    return "border-[#FFA26B] text-[#FF9F43]";
  }

  if (normalized.startsWith("dinas")) {
    return "border-[#B58BFF] text-[#8C5CE0]";
  }

  return "border-[#48d9a9] text-[#32c896]";
};

const MealDetailItem = ({
  item,
  activeTab,
  isCleaningService,
  canComplete,
  isCompleting,
  onComplete,
}: MealDetailItemProps) => {
  const normalizedStatus = (item.status ?? "").trim().toLowerCase();
  const isFinished = normalizedStatus === "finished";
  const isNotTakingMealTab = activeTab === NOT_TAKING_MEAL_TAB_TITLE;
  const isUnallocatedTab = activeTab === UNALLOCATED_TAB_TITLE;

  const initials =
    item.full_name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((value) => value.charAt(0))
      .join("")
      .toUpperCase() || "?";

  return (
    <div className="relative flex min-h-[78px] items-center overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="absolute bottom-0 left-0 top-0 w-[6px] rounded-l-xl bg-[#ff706b]" />

      <div className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#aeb5d8] text-[13px] font-semibold text-white">
          {initials}
        </div>

        <div className="min-w-0 flex-1 overflow-hidden">
          <div className="flex min-w-0 flex-nowrap items-center gap-2 overflow-hidden">
            <p className="min-w-0 flex-1 truncate whitespace-nowrap text-[13px] font-semibold text-[#555]">
              {item.full_name}
            </p>

            {item.check_in_time && (
              <span className="shrink-0 whitespace-nowrap text-[10px] text-gray-300">
                {item.check_in_time}
              </span>
            )}
          </div>

          {item.type !== "External" && (
            <div className="mt-1 flex min-w-0 flex-nowrap items-center gap-1.5 overflow-hidden whitespace-nowrap">
              <span className="shrink-0 text-[10px] text-gray-400">
                {item.nrp}
              </span>

              {item.department && (
                <>
                  <span className="shrink-0 text-[10px] text-gray-300">•</span>

                  <span className="min-w-0 max-w-[120px] truncate whitespace-nowrap text-[10px] text-gray-400">
                    {item.department}
                  </span>
                </>
              )}

              {isNotTakingMealTab && (item.reason || item.type) && (
                <>
                  <span className="shrink-0 text-[10px] text-gray-300">•</span>

                  <span
                    className={`shrink-0 whitespace-nowrap rounded border px-2 py-0.5 text-[9px] font-medium ${getReasonBadgeClassName(
                      item.reason ?? item.type,
                    )}`}
                  >
                    {item.reason ?? item.type}
                  </span>
                </>
              )}

              {isUnallocatedTab && item.type && (
                <>
                  <span className="shrink-0 text-[10px] text-gray-300">•</span>

                  <span
                    className={`shrink-0 whitespace-nowrap rounded border px-2 py-0.5 text-[9px] font-medium ${getTypeBadgeClassName(
                      item.type,
                    )}`}
                  >
                    {item.type}
                  </span>
                </>
              )}
            </div>
          )}

          {!isNotTakingMealTab && !isUnallocatedTab && (
            <div className="mt-1 flex min-w-0 flex-nowrap items-center gap-1.5 overflow-hidden whitespace-nowrap">
              {item.category && (
                <span className="shrink-0 whitespace-nowrap rounded-md border border-[#48d9a9] bg-[#eafaf4] px-2 py-0.5 text-[9px] font-bold text-[#32c896]">
                  {item.category}
                </span>
              )}

              {item.category && item.menu_name && (
                <span className="shrink-0 text-[10px] text-gray-300">•</span>
              )}

              {item.menu_name && (
                <span className="min-w-0 truncate whitespace-nowrap text-[10px] font-medium text-[#777]">
                  {item.menu_name}
                </span>
              )}
            </div>
          )}
        </div>

        {!isNotTakingMealTab &&
          (isCleaningService ? (
            <button
              type="button"
              disabled={!canComplete || isCompleting}
              onClick={() => {
                if (canComplete && !isCompleting) {
                  onComplete(item);
                }
              }}
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition ${
                isFinished
                  ? "bg-[#ff706b] text-white"
                  : canComplete
                    ? "bg-[#f5f5f5] text-[#c4c4c4] active:scale-95"
                    : "cursor-default bg-[#f5f5f5] text-[#c4c4c4]"
              }`}
            >
              {isCompleting ? (
                <PuffLoader color="#ff706b" size={16} />
              ) : isFinished ? (
                <FiCheck className="text-[17px]" />
              ) : (
                <FiCheckSquare className="text-[17px]" />
              )}
            </button>
          ) : (
            <div
              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                isFinished
                  ? "bg-[#ff706b] text-white"
                  : "bg-[#f5f5f5] text-[#c4c4c4]"
              }`}
            >
              {isFinished ? (
                <FiCheck className="text-[17px]" />
              ) : (
                <FiCheckSquare className="text-[17px]" />
              )}
            </div>
          ))}
      </div>
    </div>
  );
};

export default MealDetailItem;
